import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import AdminSearch from "./admin-search";
import "../styles/admin-page-header.css";

export interface Crumb {
    /** i18n key for the crumb label. */
    labelKey: string;
    /** Route to navigate to when the crumb is clicked. Omit on the leaf
     *  (current page) so it renders as plain text. */
    to?: string;
}

interface AdminPageHeaderProps {
    /** Translation key for the page title (rendered as <h1>). */
    titleKey: string;
    /** Parent breadcrumbs (root → leaf parent). The current page is NOT
     *  repeated as a crumb — the title acts as the leaf. */
    crumbs?: Crumb[];
    /** Where the back arrow navigates to. Defaults to history-back. */
    backTo?: string;
    /** When true, the search bar is omitted (e.g. on the admin hub itself
     *  where the page IS the search target). Defaults to false. */
    hideSearch?: boolean;
    /** Extra controls rendered to the left of the back button (e.g. an
     *  "Add User" button on the Users page). */
    actions?: ReactNode;
}

// Shared header for every admin-area page. Renders three rows:
//   1. breadcrumbs    (e.g. "Admin › Settings")
//   2. title + back   (matches the existing page-title-bar look)
//   3. search bar     (filters the static admin destinations index)
const AdminPageHeader = ({ titleKey, crumbs, backTo, hideSearch, actions }: AdminPageHeaderProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Mirror the visible page title in the browser tab — "Portal - {title}".
    usePageTitle(titleKey);

    // Strip a leading "admin_panel" crumb because the SideNav already
    // signals the section context — it's redundant and visually noisy.
    // Keeping the filter here means individual page callers don't need
    // to change. If "admin_panel" appears anywhere other than position 0
    // it stays (defensive against unexpected nestings).
    const visibleCrumbs = crumbs && crumbs.length > 0 && crumbs[0].labelKey === "admin_panel"
        ? crumbs.slice(1)
        : (crumbs ?? []);

    return (
        <section className="admin-page-header">
            {visibleCrumbs.length > 0 && (
                <nav className="admin-breadcrumbs" aria-label="Breadcrumb">
                    {visibleCrumbs.map((c, i) => (
                        <span key={`${c.labelKey}-${i}`} className="admin-breadcrumb">
                            {i > 0 && <ChevronRight size={14} className="admin-breadcrumb-sep" />}
                            {c.to ? (
                                <Link to={c.to} className="admin-breadcrumb-link">{t(c.labelKey)}</Link>
                            ) : (
                                <span className="admin-breadcrumb-current">{t(c.labelKey)}</span>
                            )}
                        </span>
                    ))}
                </nav>
            )}

            <div className="page-title-bar">
                <h1 className="page-title">{t(titleKey)}</h1>
                <div className="admin-page-header-actions">
                    {actions}
                    <button
                        className="page-back-btn"
                        onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
                        aria-label={t("back")}
                    >
                        <ArrowLeft size={20} />
                    </button>
                </div>
            </div>

            {!hideSearch && <AdminSearch />}
        </section>
    );
};

export default AdminPageHeader;
